import { Analytics } from '@cobliteam/analytics';
import { Location, type Media, type MediaEvent, type MediaRequest, MediaType } from '@cobliteam/common-types';
import { useCreateMediaShareLink } from '@cobliteam/hooks/src/useCamera/useCameraMedia';
import { useTranslation } from '@cobliteam/i18n';
import { Menu } from '@cobliteam/ui/components';
import Alert from '@cobliteam/ui/components/Alert';
import Icon from '@cobliteam/ui/icons';
import { format } from '@cobliteam/utils/date';
import { getMediaEventLabel, getSeverityTagLabel, MediaTableType } from '../../../utils/mediaUtils';

interface MenuItemShareMediaProps {
  media?: Media;
  isOnTable?: boolean;
}

const isMobile = () => window.innerWidth <= 800 || window.innerHeight <= 600;

const MenuItemShareMedia = ({ media, isOnTable }: MenuItemShareMediaProps) => {
  const isEventMedia = media?.mediaType === MediaType.EVENT_VIDEO;
  const page = isEventMedia ? MediaTableType.EVENT_MEDIA : MediaTableType.FOOTAGE_MEDIA;
  const { t } = useTranslation('media');

  const { mutateAsync } = useCreateMediaShareLink();

  const generateMessage = async () => {
    if (!media) return;

    const data = await mutateAsync(
      { mediaId: media.id },
      {
        onError: () => {
          Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
        },
      }
    );

    if (!data) return;

    const { driver, severity, timestamp } = media;
    const { sharableMediaUrl, expirationTimestamp } = data;

    const dateFormat = "dd 'de' LLLL 'de' yyyy";

    const expirationDate = expirationTimestamp && format(expirationTimestamp, dateFormat);
    const mediaDate = timestamp && format(timestamp, dateFormat);

    const eventDetails = media?.mediaDetails as MediaEvent;
    const requestDetails = media?.mediaDetails as MediaRequest;

    const driverName = driver?.name;
    const requestedBy = requestDetails.createdBy?.fullName;
    const requestDate = requestDetails.createdAt && format(requestDetails.createdAt, dateFormat);

    const messageContent = isEventMedia
      ? `Olá, estou compartilhando com você um vídeo de ${getMediaEventLabel(eventDetails.eventTrigger, t).toLowerCase()} de nível ${getSeverityTagLabel(t, severity).toLowerCase()} do dia ${mediaDate}. Para assistir, acesse o link: ${sharableMediaUrl} Válido até: ${expirationDate}`
      : `Olá, estou compartilhando com você um vídeo solicitado ${requestedBy ? ` por ${requestedBy}` : ''} no dia ${requestDate}, referente ao dia ${mediaDate}${
          driverName ? ` do motorista ${driverName}.` : '.'
        } Para assistir, acesse o link: ${sharableMediaUrl} Válido até: ${expirationDate}`;

    return messageContent;
  };

  const generateWhatsappLink = (message: string) => {
    const encodedLink = encodeURIComponent(message);
    return isMobile() ? `whatsapp://send?text=${encodedLink}` : `https://web.whatsapp.com/send?text=${encodedLink}`;
  };

  const getVideoURL = () => {
    if (!media) return;
    const origin = window.location.origin;

    const locationPath = isEventMedia ? Location.CAMERA_EVENTS_DETAILS : Location.CAMERA_REQUESTS_DETAILS;
    const path = locationPath.replace(':mediaId', media.id);
    const url = new URL(path, origin);

    return url.toString();
  };

  const handleWhatsappClick = async () => {
    try {
      const message = await generateMessage();
      if (message) {
        const url = generateWhatsappLink(message);
        Analytics.track('Media Share WhatsApp Link Clicked', {
          page,
          isFavorite: media?.isFavorite,
          isOnTable: isOnTable,
          videoURL: getVideoURL(),
          severity: media?.severity,
        });
        window.open(url);
      } else {
        Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
      }
    } catch (error) {
      Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
    }
  };

  const handleCopyLink = async () => {
    try {
      const message = await generateMessage();
      Analytics.track('Event Share Clipboard Link Clicked', {
        page,
        isFavorite: media?.isFavorite,
        isOnTable: isOnTable,
        videoURL: getVideoURL(),
        severity: media?.severity,
      });

      if (!message) throw new Error();
      await navigator.clipboard.writeText(message);

      Alert.success('Copiado para a área de transferência.');
    } catch (error) {
      Alert.error('Erro ao compartilhar o vídeo. Tente novamente mais tarde.');
    }
  };

  return (
    <>
      <Menu.Item onClick={handleCopyLink} className='w-full' disabled={!media}>
        <Menu.Icon icon={<Icon name='link' />} />
        <Menu.Label>Copiar link do vídeo</Menu.Label>
      </Menu.Item>
      <Menu.Item onClick={handleWhatsappClick} className='w-full' disabled={!media}>
        <Menu.Icon icon={<Icon name='cb-whatsapp' />} />
        <Menu.Label>Enviar por whatsapp</Menu.Label>
      </Menu.Item>
    </>
  );
};

export default MenuItemShareMedia;
